import {MenuItem, TextField} from '@mui/material';

export type SelectFilterPrimitive = string | boolean;

export type SelectFilterOption<
  T extends SelectFilterPrimitive = SelectFilterPrimitive,
> = {
  value: T | null;
  label: string;
};

export type SelectFilterConfig<
  T extends SelectFilterPrimitive = SelectFilterPrimitive,
> = {
  placeholder: string;
  options: SelectFilterOption<T>[];
};

export type BooleanFilterOption = SelectFilterOption<boolean>;
export type BooleanFilterConfig = SelectFilterConfig<boolean>;

type Props<T extends SelectFilterPrimitive = SelectFilterPrimitive> = {
  value: T | null;
  onChange: (v: T | null) => void;
  options: SelectFilterOption<T>[];
};

export const SelectFilter = <T extends SelectFilterPrimitive>({
  value,
  onChange,
  options,
}: Props<T>) => {
  const selectedOptionIndex =
    value === null ? -1 : options.findIndex(option => option.value === value);

  return (
    <TextField
      select
      size="small"
      value={selectedOptionIndex === -1 ? '' : String(selectedOptionIndex)}
      sx={{
        '& .MuiInputBase-root': {
          height: 40,
          minWidth: 160,
        },
      }}
      SelectProps={{
        displayEmpty: true,
        renderValue: v => {
          if (v === '') {
            return <span style={{color: '#9e9e9e'}}>{options[0]?.label}</span>;
          }

          return options[Number(v)]?.label;
        },
      }}
      onChange={e => {
        const nextValue = e.target.value;

        if (nextValue === '') {
          onChange(null);
          return;
        }

        onChange(options[Number(nextValue)]?.value ?? null);
      }}
    >
      {options.map((o, index) => (
        <MenuItem
          key={String(o.value)}
          value={o.value === null ? '' : String(index)}
        >
          {o.value === null ? (
            <span style={{color: '#9e9e9e'}}>{o.label}</span>
          ) : (
            o.label
          )}
        </MenuItem>
      ))}
    </TextField>
  );
};
